import React from "react";

import { Stack, SubmissionForm, useTheme } from "@bps/fluent-ui";
import { UseFormMethods } from "@bps/form-api/dist/src/lib/form-api.types";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { SoftwarePackageVersion } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useCreateSoftwarePackageDefault,
  useUpdateSoftwarePackageDefault
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SoftwarePackageDefaultSummaryDto } from "./";
import { SoftwarePackageDefaultFormFields } from "./SoftwarePackageDefaultFormFields";

interface SoftwarePackageDefaultFormValues {
  softwarePackageVersionId: string;
}

interface SoftwarePackageDefaultFormProps {
  softwarePackage: SoftwarePackageDefaultSummaryDto;
  softwarePackageVersions: SoftwarePackageVersion[];
}

export const SoftwarePackageDefaultForm: React.FC<SoftwarePackageDefaultFormProps> = ({
  softwarePackage,
  softwarePackageVersions
}) => {
  const theme = useTheme();
  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const {
    mutateAsync: updateSoftwarePackageDefault
  } = useUpdateSoftwarePackageDefault();

  const {
    mutateAsync: createSoftwarePackageDefault
  } = useCreateSoftwarePackageDefault();

  const onSubmit = async (
    formValues: SoftwarePackageDefaultFormValues,
    actions: Pick<
      UseFormMethods<SoftwarePackageDefaultFormValues>,
      "reset" | "setValue"
    >
  ) => {
    const { softwarePackageVersionId } = formValues;
    const {
      id,
      softwarePackageId,
      eTag,
      changeLog,
      installType
    } = softwarePackage;

    const request = {
      id,
      softwarePackageId,
      softwarePackageVersionId:
        softwarePackageVersionId ?? softwarePackage.softwarePackageVersionId,
      installType,
      changeLog,
      eTag
    };

    if (softwarePackage.id === undefined) {
      // Create a software package default for the service
      try {
        await createSoftwarePackageDefault(request);
        actions.reset({ softwarePackageVersionId });
      } catch (e) {
        return e;
      }
    } else {
      // Update a software package default for the service
      try {
        await updateSoftwarePackageDefault(request);
        actions.reset({ softwarePackageVersionId });
      } catch (e) {
        return e;
      }
    }
  };

  return (
    <SubmissionForm<SoftwarePackageDefaultFormValues>
      initialValues={{
        softwarePackageVersionId: softwarePackage.softwarePackageVersionId
      }}
      onSubmit={onSubmit}
      hideButtons
      disabled={!hasDeployPermission}
    >
      {() => (
        <Stack
          horizontal
          verticalAlign="center"
          tokens={{ childrenGap: theme.spacing.m }}
        >
          <SoftwarePackageDefaultFormFields
            softwarePackageVersions={softwarePackageVersions}
          />
        </Stack>
      )}
    </SubmissionForm>
  );
};
